import * as React from "react";
import { useEffect } from "react";
import apiClient from "@/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z as zod } from "zod";

import { paths } from "@/paths";
import { logger } from "@/lib/default-logger";
import { CenteredLayout } from "@/components/auth/centered-layout";
import { RouterLink } from "@/components/core/link";
import { DynamicLogo } from "@/components/core/logo";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

const schema = zod.object({
	email: zod.string().min(1, { message: "Email is required" }).email(),
});

type Values = zod.infer<typeof schema>;

export function ForgotPassword(): React.JSX.Element {
	const navigate = useNavigate();
	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<Values>({ defaultValues: { email: "" }, resolver: zodResolver(schema) });

	useEffect(() => {
		localStorage.removeItem("token");
	}, []);

	const onSubmit = async (data: Values) => {
		try {
			await apiClient.post("/email-verification/send-password-reset", {
				email: data.email,
			});

			toast.success("Recovery link sent to your email");

			navigate("/sign-in");
		} catch (error) {
			logger.error(error);
			toast.error("Something went wrong, unable to send recovery link");
		}
	};

	return (
		<React.Fragment>
			<CenteredLayout>
				<Stack spacing={4}>
					<div>
						<Box
							component={RouterLink}
							href={paths.home}
							sx={{ display: "flex", fontSize: 0, justifyContent: "center" }}
						>
							<DynamicLogo colorDark="light" colorLight="dark" height={42} width={222} />
						</Box>
					</div>
					<Card>
						<CardHeader title="Forgot password" subheader="Enter your email to reset your password" />
						<CardContent>
							<form onSubmit={handleSubmit(onSubmit)}>
								<Stack spacing={2}>
									<Stack spacing={2}>
										<Controller
											control={control}
											name="email"
											render={({ field }) => (
												<FormControl error={Boolean(errors.email)}>
													<InputLabel>Email address</InputLabel>
													<OutlinedInput {...field} type="email" />
													{errors.email ? <CustomFormHelperError text={errors.email.message || ""} /> : null}
												</FormControl>
											)}
										/>

										<Button type="submit" variant="contained" loading={isSubmitting}>
											Send recovery link
										</Button>
									</Stack>
									<div>
										<Link component={RouterLink} href="/sign-in" variant="subtitle2">
											Back to sign in
										</Link>
									</div>
								</Stack>
							</form>
						</CardContent>
					</Card>
				</Stack>
			</CenteredLayout>
		</React.Fragment>
	);
}
